/**
 * Which limit checks the interactive table can show/hide, mirroring
 * RiskLimits_Frontend's STRATEGY_TYPE_LIMITS checkbox list (config/utilizationMetrics.js)
 * but against data360's own live exposure fields (lmv/smv/gross_exposure) rather
 * than RiskLimits' simulation-derived total_lmv/total_smv/total_gross.
 *
 * current/cap are already on the same 0-100 percent-of-risk-capital scale —
 * see riskLimitsService.getUtilization on the backend.
 */
export const LIMIT_METRICS = [
  {
    key: "lmv",
    label: "Max LMV %",
    currentLabel: "LMV / RC",
    numeratorField: "lmv",
    currentPctField: "lmv_utilization_pct",
    capField: "max_lmv_pct",
    defaultEnabled: true,
  },
  {
    key: "smv",
    label: "Max SMV %",
    currentLabel: "SMV / RC",
    numeratorField: "smv",
    currentPctField: "smv_utilization_pct",
    capField: "max_smv_pct",
    defaultEnabled: false,
  },
  {
    key: "gmv",
    label: "Max GMV %",
    currentLabel: "GMV / RC",
    numeratorField: "gross_exposure",
    currentPctField: "gmv_utilization_pct",
    capField: "max_gmv_pct",
    defaultEnabled: true,
  },
  {
    key: "nmv",
    label: "Max NMV %",
    currentLabel: "NMV / RC",
    numeratorField: "net_exposure",
    currentPctField: "nmv_utilization_pct",
    capField: "max_nmv_pct",
    defaultEnabled: false,
  },
];

export const DEFAULT_ENABLED_METRIC_KEYS = LIMIT_METRICS.filter((m) => m.defaultEnabled).map((m) => m.key);

/**
 * status: "no-limit" (cap not configured), "under", "at-limit", "over".
 * deltaPp: currentPct - capPct, in percentage points — null when no cap is set.
 */
export function computeVsMax(currentPct, capPct) {
  if (currentPct == null || !Number.isFinite(currentPct)) {
    return { status: "incomplete", deltaPp: null };
  }
  const cap = Number(capPct);
  if (!Number.isFinite(cap) || cap <= 0) {
    return { status: "no-limit", deltaPp: null };
  }
  const deltaPp = Math.abs(currentPct) - cap;
  if (deltaPp > 0.05) return { status: "over", deltaPp };
  if (deltaPp < -0.05) return { status: "under", deltaPp };
  return { status: "at-limit", deltaPp };
}

// One status palette for every "vs Max %" figure (strategy cards, limits table, threshold admin); the same hues as
// the cards on the Firmwide Summary page.
export const LIMIT_STATUS_COLOR = { good: "#059669", warning: "#d97706", critical: "#dc2626" };
export const LIMIT_STATUS_LABEL = { good: "Within Limit", warning: "Near Limit", critical: "Over Limit" };

/**
 * How much of a Max % limit is used. current/cap are both % of risk capital (NMV is compared by magnitude).
 * status: "good" up to 95% of the cap, "warning" above 95%, "critical" above 100%; null when either is missing
 * or no cap is set.
 */
export function limitUsage(currentPct, capPct) {
  if (currentPct == null || capPct == null || currentPct === "" || capPct === "") return null;
  const current = Math.abs(Number(currentPct));
  const cap = Number(capPct);
  if (!Number.isFinite(current) || !Number.isFinite(cap) || cap <= 0) return null;
  const usedPct = (current / cap) * 100;
  const status = usedPct > 100 ? "critical" : usedPct > 95 ? "warning" : "good";
  return { current, cap, usedPct, status };
}

export function getUtilizationStatus(percentOfCap) {
  if (percentOfCap == null || !Number.isFinite(percentOfCap)) return "no-limit";
  if (percentOfCap > 100) return "critical";
  if (percentOfCap > 80) return "warning";
  return "good";
}
